// frontend/src/hooks/useAdminBookingWebSocket.jsx

import { useState, useEffect, useRef, useCallback } from 'react';
import { createAuthenticatedWebSocket } from '../utils/webSocketAuth';
import {
    classifyWebSocketClose,
    getTokenSessionId,
    handleTerminalSessionFailure,
} from '../utils/authUtils';
import logger from '../utils/logger';

/**
 * Custom hook for admin booking real-time updates via WebSocket
 * Connects to /ws/entities/{entityId}/admin/bookings
 *
 * SECURITY: Passes JWT token for server-side validation
 *
 * @param {number} entityId - Entity ID to subscribe to
 * @param {boolean} isAdmin - Whether user is admin (only connects if true)
 * @param {Function} onBookingEvent - Callback when booking event received
 * @returns {{ connectionStatus: string, isConnected: boolean }}
 */
export const useAdminBookingWebSocket = (entityId, isAdmin, onBookingEvent) => {
    const [connectionStatus, setConnectionStatus] = useState('disconnected');
    const wsRef = useRef(null);
    const pingIntervalRef = useRef(null); // Track ping interval for cleanup
    const reconnectTimeoutRef = useRef(null);
    const reconnectAttemptsRef = useRef(0);
    const denialLatchRef = useRef(null);
    const bookingEventRef = useRef(onBookingEvent);
    const maxReconnectAttempts = 5;
    const baseReconnectDelay = 3000;

    useEffect(() => {
        bookingEventRef.current = onBookingEvent;
    }, [onBookingEvent]);

    // Get WebSocket URL. JWT auth is sent with a WebSocket subprotocol so it
    // does not appear in URLs, browser history, proxies, or URL logs.
    // Use same URL construction pattern as useTripsWebSocket for consistency
    const getWsUrl = useCallback(() => {
        const baseWsUrl = import.meta.env.VITE_WS_URL || window.location.origin.replace('http', 'ws');
        return `${baseWsUrl}/ws/entities/${entityId}/admin/bookings`;
    }, [entityId]);

    // Handle incoming messages
    const handleMessage = useCallback((event, onAuthorized) => {
        try {
            const message = typeof event.data === 'string' ? event.data.trim() : event.data;

            // The backend heartbeat response is intentionally plain text, not a
            // JSON event envelope. Handle that narrow keepalive path before
            // parsing so expected `pong` traffic does not look like a broken
            // booking payload.
            if (message === 'pong') {
                return;
            }

            const data = JSON.parse(event.data);

            if (data.type === 'connected') {
                onAuthorized();
                return;
            }

            // Handle admin-channel events used by booking management and
            // driver availability live view.
            if ([
                'booking_created',
                'booking_updated',
                'booking_canceled',
                'driver_availability_changed'
            ].includes(data.type)) {
                if (bookingEventRef.current) {
                    bookingEventRef.current({
                        type: data.type,
                        ...data.payload
                    });
                }
            }
        } catch {
            // Parser errors can echo untrusted frame content, so neither the
            // exception nor its message is safe to log here.
            logger.error('Error parsing admin booking WebSocket message', {
                message: 'Invalid WebSocket message payload'
            });
        }
    }, []);

    // Connect to WebSocket
    const connect = useCallback(() => {
        if (!entityId || !isAdmin) return;

        // Avoid noisy reconnect loops if auth token is missing
        const token = localStorage.getItem('token');
        if (!token) {
            setConnectionStatus('error');
            console.warn('Admin booking WebSocket skipped - missing auth token');
            return;
        }

        // JWT rotation retains jti, so use the database-session identity for
        // the latch. A new bearer for the same session must not authorize
        // another policy retry.
        const denialKey = `${getTokenSessionId(token) || token}:${entityId}`;
        if (denialLatchRef.current === denialKey) {
            setConnectionStatus('access_denied');
            return;
        }
        denialLatchRef.current = null;

        // Avoid reconnect loops if we're already connected/connecting
        if (wsRef.current) {
            if ([WebSocket.OPEN, WebSocket.CONNECTING].includes(wsRef.current.readyState)) {
                return;
            }
            wsRef.current = null;
        }

        setConnectionStatus('connecting');

        const wsUrl = getWsUrl();
        console.log('[useAdminBookingWebSocket] Connecting to:', wsUrl);

        const authBearer = token;
        const ws = createAuthenticatedWebSocket(wsUrl, authBearer);
        wsRef.current = ws;

        ws.onopen = () => {
            if (wsRef.current !== ws) return;
            // The transport is open, but the server has not yet completed its
            // database-backed endpoint authorization. Stay `connecting` until
            // the authoritative `connected` frame arrives.
            console.log('Admin booking WebSocket transport opened');
        };

        ws.onmessage = (event) => {
            if (wsRef.current === ws) {
                handleMessage(event, () => {
                    setConnectionStatus('connected');
                    reconnectAttemptsRef.current = 0;
                    if (!pingIntervalRef.current) {
                        pingIntervalRef.current = setInterval(() => {
                            if (wsRef.current === ws && ws.readyState === WebSocket.OPEN) {
                                ws.send('ping');
                            }
                        }, 30000);
                    }
                });
            }
        };

        ws.onerror = (error) => {
            if (wsRef.current !== ws) return;
            console.error('Admin booking WebSocket error:', error);
            setConnectionStatus('error');
        };

        ws.onclose = (event) => {
            if (wsRef.current !== ws) return;
            console.log('Admin booking WebSocket closed:', event.code, event.reason);
            setConnectionStatus('disconnected');
            wsRef.current = null;

            // IMPORTANT: Clear ping interval on ANY close to prevent orphaned intervals
            if (pingIntervalRef.current) {
                clearInterval(pingIntervalRef.current);
                pingIntervalRef.current = null;
            }

            const closeClassification = classifyWebSocketClose(event.code, event.reason);
            if (closeClassification.category === 'session_invalid') {
                if (reconnectTimeoutRef.current) {
                    clearTimeout(reconnectTimeoutRef.current);
                    reconnectTimeoutRef.current = null;
                }
                setConnectionStatus('auth_expired');
                void handleTerminalSessionFailure({
                    expectedToken: authBearer,
                    correlation: closeClassification.correlation,
                });
                return;
            }

            if (['static_denial', 'mutable_denial'].includes(closeClassification.category)) {
                denialLatchRef.current = denialKey;
                setConnectionStatus('access_denied');
                return;
            }

            // Attempt reconnection if not intentionally closed
            if (event.code !== 1000 && reconnectAttemptsRef.current < maxReconnectAttempts) {
                const delay = baseReconnectDelay * Math.pow(2, reconnectAttemptsRef.current);
                reconnectAttemptsRef.current++;

                console.log(`Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current})`);
                setConnectionStatus('reconnecting');

                reconnectTimeoutRef.current = setTimeout(() => {
                    connect();
                }, delay);
            }
        };

    }, [entityId, isAdmin, getWsUrl, handleMessage]);

    // Disconnect - cleans up all resources
    const disconnect = useCallback(() => {
        // Clear reconnect timeout
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }

        // Clear ping interval
        if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
            pingIntervalRef.current = null;
        }

        // Close WebSocket
        if (wsRef.current) {
            wsRef.current.close(1000, 'Component unmounting');
            wsRef.current = null;
        }

        setConnectionStatus('disconnected');
    }, []);

    // Connect on mount, disconnect on unmount
    useEffect(() => {
        if (entityId && isAdmin) {
            connect();
        }

        return () => {
            disconnect();
        };
    }, [entityId, isAdmin, connect, disconnect]);

    // Handle page visibility changes
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden) {
                // Page hidden - disconnect to save resources
                disconnect();
            } else {
                // Page visible - reconnect
                if (entityId && isAdmin) {
                    reconnectAttemptsRef.current = 0;
                    connect();
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [entityId, isAdmin, connect, disconnect]);

    return {
        connectionStatus,
        isConnected: connectionStatus === 'connected'
    };
};

export default useAdminBookingWebSocket;
